/**
 * RTWO Asset & Token Engine — Engine Implementation
 * Phase 14 | Module ID: RTWO-ASST-001
 * Version: 1.0.0-phase14
 *
 * Coordinates all Asset & Token Engine services through dependency injection,
 * in-memory repositories, adapter registries, and the internal event bus.
 *
 * Truthful Data Policy:
 * - No fake assets, token supplies, holders, prices, transfers, or metadata.
 * - Tokens are NOT claimed as live or operational.
 * - All operations return placeholder results until real backend is connected.
 *
 * Source: Asset & Token Engine (RTWO-ASST-001)
 */

import { ASST_ENGINE_VERSION } from './domain-models';
import type {
  IASSService,
  ASSServiceRegistry,
  IAssetRegistryService,
  ITokenStandardRegistryService,
  ISupplyPolicyService,
  IPermissionService,
  ITransferRuleService,
  IMetadataService,
  IAssetEventPublisherService,
  IAssetValidationService,
  IAssetReadinessService,
  ITokenLifecycleService,
} from './service-interfaces';
import { ASSServiceRegistry as ASSServiceRegistryImpl } from './registry/ass-service-registry';
import { createASSEngineServices } from './services';

export class RTWOAssetTokenEngine {
  private static _instance: RTWOAssetTokenEngine | null = null;

  readonly moduleId = ASST_ENGINE_VERSION.moduleId;
  readonly moduleName = ASST_ENGINE_VERSION.moduleName;
  readonly version = ASST_ENGINE_VERSION.version;
  readonly phase = ASST_ENGINE_VERSION.phase;
  readonly status = ASST_ENGINE_VERSION.status;
  readonly productionReadiness = ASST_ENGINE_VERSION.productionReadiness;

  readonly assetRegistry: IAssetRegistryService;
  readonly tokenStandardRegistry: ITokenStandardRegistryService;
  readonly supplyPolicyService: ISupplyPolicyService;
  readonly permissionService: IPermissionService;
  readonly transferRuleService: ITransferRuleService;
  readonly metadataService: IMetadataService;
  readonly assetEventPublisher: IAssetEventPublisherService;
  readonly assetValidationService: IAssetValidationService;
  readonly assetReadinessService: IAssetReadinessService;
  readonly tokenLifecycleService: ITokenLifecycleService;

  private readonly _serviceRegistry = new ASSServiceRegistryImpl();

  private constructor() {
    const { services } = createASSEngineServices();

    this.assetRegistry = services.assetRegistry;
    this.tokenStandardRegistry = services.tokenStandardRegistry;
    this.supplyPolicyService = services.supplyPolicyService;
    this.permissionService = services.permissionService;
    this.transferRuleService = services.transferRuleService;
    this.metadataService = services.metadataService;
    this.assetEventPublisher = services.assetEventPublisher;
    this.assetValidationService = services.assetValidationService;
    this.assetReadinessService = services.assetReadinessService;
    this.tokenLifecycleService = services.tokenLifecycleService;

    const serviceList: IASSService[] = [
      this.assetRegistry,
      this.tokenStandardRegistry,
      this.supplyPolicyService,
      this.permissionService,
      this.transferRuleService,
      this.metadataService,
      this.assetEventPublisher,
      this.assetValidationService,
      this.assetReadinessService,
      this.tokenLifecycleService,
    ];

    for (const service of serviceList) {
      this._serviceRegistry.register(service);
    }
  }

  static getInstance(): RTWOAssetTokenEngine {
    if (!RTWOAssetTokenEngine._instance) {
      RTWOAssetTokenEngine._instance = new RTWOAssetTokenEngine();
    }
    return RTWOAssetTokenEngine._instance;
  }

  getServiceRegistry(): ASSServiceRegistry {
    const services = this._serviceRegistry.getAll().map((service) => service.getDescriptor());

    return {
      services,
      totalServices: services.length,
      structureReadyServices: services.filter((s) => s.status === 'structure_ready').length,
      waitingForBackendServices: services.filter(
        (s) =>
          s.status === 'waiting_for_ledger_connection' || s.status === 'waiting_for_token_runtime'
      ).length,
      source: 'Asset & Token Engine (RTWO-ASST-001)',
    };
  }

  async initialize(): Promise<void> {
    await Promise.all(this._serviceRegistry.getAll().map((service) => service.initialize()));
  }

  getAllServices(): IASSService[] {
    return this._serviceRegistry.getAll();
  }
}

export const getAssetTokenEngine = (): RTWOAssetTokenEngine => RTWOAssetTokenEngine.getInstance();

export default RTWOAssetTokenEngine;
